import { useState } from 'react';

import clsx from 'clsx';

import Accordion from 'components/Accordion';
import CircularProgress from 'components/CircularProgress';
import CtaButton from 'components/CtaButton';
import Typography from 'components/Typography';
import { useAppContext } from 'context/AppContext';
import { useEventContext } from 'context/EventContext';
import { revealSEOUrl } from 'helpers/SEOHelper';
import {
  FACETS_EVENT_NAMES,
  TITLES,
  buildDialogDictionary,
  shouldRenderAllSeoFacetsForBot,
  shouldRenderSeoFacetHref,
} from 'helpers/filters';
import setRatingDistributionValues from 'helpers/setRatingDistributionValues';
import { STATUS } from 'helpers/spa';

import Dialog from '../../../../components/Dialog';
import FormControlLabel from '../../../FormControlLabel';

import classes from './Default.module.scss';

const Default = ({
  facet,
  onExpandClick,
  onItemClick,
  status,
  facetKey,
  category,
}) => {
  const { sendEvent } = useEventContext();
  const { route, query, userAgent } = useAppContext();

  const categoryHasSeoFacets = category?.has_seo_facets || false;

  const [showDialog, setShowDialog] = useState(false);
  const MAX_FACETS = facet.length <= 9 ? 9 : 5;
  const loading = status === STATUS.loading;
  const renderAllSeoFacetsForBot = shouldRenderAllSeoFacetsForBot({
    facetKey,
    isBot: userAgent?.isBot,
    route,
    query,
    categoryHasSeoFacets,
  });

  const facetsParsed = renderAllSeoFacetsForBot
    ? facet.filter((facetType) => facetType.is_seo)
    : facet.filter(
        (facetType, index) => index < MAX_FACETS || facetType.selected
      );

  const handleViewAllClick = () => {
    sendEvent({
      engagementComponent: 'filter',
      engagementDestination: 'filter_see-all-options',
      engagementDetails: `filter-expand-button_view-all_${FACETS_EVENT_NAMES[facetKey]}`,
      name: 'engagement_filter',
    });

    setShowDialog(() => true);
  };

  return (
    <Accordion
      className="Default"
      defaultExpanded={true}
      detailsClassname={clsx(classes.accordionDetails, {
        [classes.accordionDetailsWithButton]:
          !renderAllSeoFacetsForBot && facet.length > MAX_FACETS,
      })}
      summary={<Typography variant="heading-sm">{TITLES[facetKey]}</Typography>}
      summaryClassname={classes.accordionSummary}
      onExpandClick={onExpandClick({ facetKey })}
    >
      {facetsParsed.length === 0 ? (
        <Typography>No filters available</Typography>
      ) : (
        facetsParsed.map((facetType, valueIndex) => (
          <FormControlLabel
            disabled={loading || facetType.count === 0}
            key={facetType.label}
            fullWidth={true}
            label={
              <span className={classes.filterRow}>
                <span>{facetType.label}</span>
                {loading ? (
                  <CircularProgress
                    className={classes.circularProgress}
                    color="inherit"
                    size={16}
                  />
                ) : (
                  !facetType.selected &&
                  facetType.count !== null && (
                    <span className={classes.count}>({facetType.count})</span>
                  )
                )}
              </span>
            }
            ratingDistributionValues={
              facetKey === 'rating_facet'
                ? setRatingDistributionValues(facet)[valueIndex]
                : {}
            }
            isChecked={facetType.selected}
            href={
              shouldRenderSeoFacetHref({
                isSeo: facetType.is_seo,
                route,
                query,
                categoryHasSeoFacets,
              })
                ? revealSEOUrl(facetType.href)
                : undefined
            }
            onClick={(event, { isChecked }) =>
              onItemClick(event, {
                facet: facetKey,
                href: revealSEOUrl(facetType.href),
                value: facetType.value,
                label: facetType.label,
                isChecked,
              })
            }
            type={facetKey === 'rating_facet' ? 'ratingDistribution' : 'text'}
          />
        ))
      )}

      {!renderAllSeoFacetsForBot && facetsParsed.length < facet.length && (
        <div className={classes.ctaButtonContainer}>
          <CtaButton
            data-testid="filters-see-full-list"
            disabled={loading}
            label="SEE FULL LIST"
            variant="text"
            onClick={handleViewAllClick}
          />
          <Dialog
            attributes={buildDialogDictionary(facet)}
            dialogFacet={facetKey}
            disabled={status === STATUS.loading}
            id={facetKey}
            isOpen={showDialog}
            onItemClick={onItemClick}
            onClose={() => setShowDialog(() => false)}
            title={TITLES[facetKey]}
            categoryHasSeoFacets={categoryHasSeoFacets}
          />
        </div>
      )}
    </Accordion>
  );
};

export default Default;
